import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService, ticketService } from '../services/api';
import { AlertTriangle, Clock, Users, TicketIcon, TrendingUp, Zap } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Dashboard.css';

const PIE_COLORS = {
  Open: '#4f7fff',
  'In Progress': '#f5a623',
  Resolved: '#3ecf8e',
  Closed: '#4a5268',
  'Awaiting Client': '#7c8cf8',
  Reopened: '#ff4f6b',
};

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="stat-card">
    <div className="stat-card__icon" style={{ background: `${color}18`, color }}>
      <Icon size={20} />
    </div>
    <div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
    </div>
  </div>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    dashboardService.getSummary().then(r => setSummary(r.data));
    ticketService.getStats().then(r => setStats(r.data));
  }, []);

  const pieData = stats?.byStatus?.map(s => ({ name: s._id, value: s.count })) || [];
  const areaData = stats?.recentVolume?.map(d => ({ date: d._id.slice(5), count: d.count })) || [];

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <h1 className="page-title">Operations Dashboard</h1>
          <p className="page-subtitle">Real-time view of support health across all clients</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/tickets/new')}>
          <Zap size={14} /> New Ticket
        </button>
      </div>

      <div className="stat-grid">
        <StatCard icon={TicketIcon} label="Open Tickets" value={summary?.openTickets ?? '—'} color="var(--accent)" />
        <StatCard icon={AlertTriangle} label="Critical Open" value={summary?.criticalOpen ?? '—'} color="var(--danger)" />
        <StatCard icon={Clock} label="SLA at Risk (24h)" value={summary?.slaAtRisk ?? '—'} color="var(--warning)" />
        <StatCard icon={Users} label="Active Clients" value={summary?.totalClients ?? '—'} color="var(--success)" />
      </div>

      <div className="dashboard__charts">
        <div className="card chart-card">
          <div className="chart-card__header">
            <TrendingUp size={16} />
            <span>Ticket Volume — Last 30 Days</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={areaData}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
              <Area type="monotone" dataKey="count" stroke="var(--accent)" fill="url(#grad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="card chart-card">
          <div className="chart-card__header">
            <TicketIcon size={16} />
            <span>Tickets by Status</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value">
                {pieData.map((entry, i) => (
                  <Cell key={i} fill={PIE_COLORS[entry.name] || '#4f7fff'} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)' }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="pie-legend">
            {pieData.map(d => (
              <div key={d.name} className="pie-legend__item">
                <span className="pie-legend__dot" style={{ background: PIE_COLORS[d.name] || '#4f7fff' }} />
                <span>{d.name}</span>
                <strong>{d.value}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
